<template>
  <span class="button-container">
    <button v-if="!hasDot" :class="['custom-button', soonOrOverdueClass(cellValue)]">
      {{ statusFormatter(cellValue).toUpperCase() }}
    </button>
    <button v-else class="custom-button">
      <span :class="['icon-dot', iconDotColorClass(cellValue)]"></span>
      <span class="status-with-padding">{{ statusFormatter(cellValue).toUpperCase() }}</span>
    </button>
  </span>
</template>

<script lang="ts">
import { AccountingDocumentStatus } from '@/models/accounting-document.model';
import { defineComponent, ref } from 'vue';
import { useI18n } from 'vue-i18n';

export default defineComponent({
  name: 'InvoiceStatusRenderer',
  props: {
    params: {
      type: Object,
      default: undefined,
    },
  },
  setup(props) {
    const { t } = useI18n();
    var cellValue = ref(AccountingDocumentStatus.Open);

    function isAccountDocumentStatus(value: AccountingDocumentStatus): value is AccountingDocumentStatus {
      return Object.values(AccountingDocumentStatus).includes(value);
    }

    if (isAccountDocumentStatus(props.params?.data.status)) {
      cellValue.value = props.params?.data.status;
    } else {
      console.warn('Unexpected status in accounting document: %s', props.params?.data.status);
    }

    const hasDot = !(
      cellValue.value === AccountingDocumentStatus.Overdue || cellValue.value === AccountingDocumentStatus.Soon
    );

    function statusFormatter(documentStatus: AccountingDocumentStatus) {
      switch (documentStatus) {
        case AccountingDocumentStatus.Settled:
          return t('viewAccounting.document_status.settled');
        case AccountingDocumentStatus.InProgress:
          return t('viewAccounting.document_status.in_progress');
        case AccountingDocumentStatus.Open:
          return t('viewAccounting.document_status.open');
        case AccountingDocumentStatus.Overdue:
          return t('viewAccounting.document_status.overdue');
        case AccountingDocumentStatus.Soon:
          return t('viewAccounting.document_status.soon');
      }
    }

    function soonOrOverdueClass(value: AccountingDocumentStatus) {
      if (value === AccountingDocumentStatus.Overdue) {
        return 'red-background-white-text';
      }
      return 'orange-background-white-text';
    }

    function iconDotColorClass(value: AccountingDocumentStatus) {
      if (value === AccountingDocumentStatus.Settled) {
        return 'light-blue-color';
      } else if (value === AccountingDocumentStatus.InProgress) {
        return 'gray-dot';
      } else {
        return 'orange-dot';
      }
    }

    return {
      cellValue,
      hasDot,
      statusFormatter,
      soonOrOverdueClass,
      iconDotColorClass,
    };
  },
});
</script>

<style lang="scss" scoped>
.button-container {
  display: flex;
  align-items: center;
}

.custom-button {
  display: inline-flex;
  align-items: center;
  border: none;
  padding: 5px 10px;
  border-radius: 100px;
  background-color: var(--white-color);
  position: relative;
}

.red-background-white-text {
  background-color: var(--scarlet-red-color);
  color: var(--white-color);
}

.orange-background-white-text {
  background-color: var(--new-primary-color);
  color: var(--white-color);
}

.icon-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
}

.orange-dot {
  background-color: var(--new-primary-color);
}

.gray-dot {
  background-color: var(--light-gray-color);
}

.light-blue-color {
  background-color: var(--primary-focus-color);
}

.status-with-padding {
  padding-left: 7px;
}
</style>
